import { bbox, mask as turfMask } from "@turf/turf";
import statesGeoJson from "./data/states";

export const getBottomCenterOffset = (map, bottomPadding = 40) => {
  if (!map) return [0, 0];
  const height = map.getContainer().clientHeight;
  // On mobile, the Filters panel is a bottom sheet that always peeks up
  // ~80px above the viewport's true bottom edge (see Filters/styled.js'
  // `bottom: calc(-100% + 80px)`), so pins/popups need that much extra
  // clearance to land fully above it instead of tucked behind it.
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  const filtersBarHeight = isMobile ? 80 : 0;
  return [0, Math.max(0, height / 2 - bottomPadding - filtersBarHeight)];
};

// Bounding box for a state's geometry, suitable for map.fitBounds(), so the
// initial camera can be fit dynamically to whatever viewport size is
// actually available instead of relying on a hand-picked zoom level.
export const getStateBounds = (stateName) => {
  const feature = statesGeoJson.features.find(
    (f) => f.properties.name === stateName
  );
  if (!feature) return null;
  const [minLng, minLat, maxLng, maxLat] = bbox(feature);
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

export const getStateFeature = (stateName) => {
  return statesGeoJson.features.find((f) => f.properties.name === stateName);
};

// A polygon covering the whole world minus the given state, for use as a
// mask layer's source geometry. Unlike filtering the "other 49 states"
// dataset for "!= this state", this has no gaps: it also covers ocean, the
// Great Lakes, and neighboring countries, none of which are part of any
// state's land polygon.
export const getStateMask = (stateName) => {
  const feature = getStateFeature(stateName);
  if (!feature) return null;
  return turfMask(feature);
};

// A polygon covering the entire world, for use as a mask layer's source
// geometry when the whole map (not just the area outside one state) needs
// a blank backdrop, e.g. the district view painting over everything before
// drawing the state's own district shapes on top.
export const worldMask = {
  type: "Feature",
  properties: {},
  geometry: {
    type: "Polygon",
    coordinates: [
      [
        [-180, -90],
        [180, -90],
        [180, 90],
        [-180, 90],
        [-180, -90],
      ],
    ],
  },
};

export const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // cross-origin access throws => definitely embedded
  }
};

export const states = [
  ["Alabama", "AL"],
  ["Alaska", "AK"],
  ["Arizona", "AZ"],
  ["Arkansas", "AR"],
  ["California", "CA"],
  ["Colorado", "CO"],
  ["Connecticut", "CT"],
  ["Delaware", "DE"],
  ["District Of Columbia", "DC"],
  ["Florida", "FL"],
  ["Georgia", "GA"],
  ["Hawaii", "HI"],
  ["Idaho", "ID"],
  ["Illinois", "IL"],
  ["Indiana", "IN"],
  ["Iowa", "IA"],
  ["Kansas", "KS"],
  ["Kentucky", "KY"],
  ["Louisiana", "LA"],
  ["Maine", "ME"],
  ["Maryland", "MD"],
  ["Massachusetts", "MA"],
  ["Michigan", "MI"],
  ["Minnesota", "MN"],
  ["Mississippi", "MS"],
  ["Missouri", "MO"],
  ["Montana", "MT"],
  ["Nebraska", "NE"],
  ["Nevada", "NV"],
  ["New Hampshire", "NH"],
  ["New Jersey", "NJ"],
  ["New Mexico", "NM"],
  ["New York", "NY"],
  ["North Carolina", "NC"],
  ["North Dakota", "ND"],
  ["Ohio", "OH"],
  ["Oklahoma", "OK"],
  ["Oregon", "OR"],
  ["Pennsylvania", "PA"],
  ["Rhode Island", "RI"],
  ["South Carolina", "SC"],
  ["South Dakota", "SD"],
  ["Tennessee", "TN"],
  ["Texas", "TX"],
  ["Utah", "UT"],
  ["Vermont", "VT"],
  ["Virginia", "VA"],
  ["Washington", "WA"],
  ["West Virginia", "WV"],
  ["Wisconsin", "WI"],
  ["Wyoming", "WY"],
];

export const abbrState = (input, to = "abbr") => {
  // var states = [
  //   ["Alabama", "AL"],
  //   ["Alaska", "AK"],
  //   ["Arizona", "AZ"],
  //   ["Arkansas", "AR"],
  //   ["California", "CA"],
  //   ["Colorado", "CO"],
  //   ["Connecticut", "CT"],
  //   ["Delaware", "DE"],
  //   ["District Of Columbia", "DC"],
  //   ["Florida", "FL"],
  //   ["Georgia", "GA"],
  //   ["Hawaii", "HI"],
  //   ["Idaho", "ID"],
  //   ["Illinois", "IL"],
  //   ["Indiana", "IN"],
  //   ["Iowa", "IA"],
  //   ["Kansas", "KS"],
  //   ["Kentucky", "KY"],
  //   ["Louisiana", "LA"],
  //   ["Maine", "ME"],
  //   ["Maryland", "MD"],
  //   ["Massachusetts", "MA"],
  //   ["Michigan", "MI"],
  //   ["Minnesota", "MN"],
  //   ["Mississippi", "MS"],
  //   ["Missouri", "MO"],
  //   ["Montana", "MT"],
  //   ["Nebraska", "NE"],
  //   ["Nevada", "NV"],
  //   ["New Hampshire", "NH"],
  //   ["New Jersey", "NJ"],
  //   ["New Mexico", "NM"],
  //   ["New York", "NY"],
  //   ["North Carolina", "NC"],
  //   ["North Dakota", "ND"],
  //   ["Ohio", "OH"],
  //   ["Oklahoma", "OK"],
  //   ["Oregon", "OR"],
  //   ["Pennsylvania", "PA"],
  //   ["Rhode Island", "RI"],
  //   ["South Carolina", "SC"],
  //   ["South Dakota", "SD"],
  //   ["Tennessee", "TN"],
  //   ["Texas", "TX"],
  //   ["Utah", "UT"],
  //   ["Vermont", "VT"],
  //   ["Virginia", "VA"],
  //   ["Washington", "WA"],
  //   ["West Virginia", "WV"],
  //   ["Wisconsin", "WI"],
  //   ["Wyoming", "WY"],
  // ];

  if (to === "abbr") {
    input = input.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });

    for (let i = 0; i < states.length; i++) {
      if (states[i][0] === input) {
        return states[i][1];
      }
    }
  } else if (to === "name") {
    input = input.toUpperCase();
    for (let i = 0; i < states.length; i++) {
      if (states[i][1] === input) {
        return states[i][0];
      }
    }
  }
};

export const normalize = (string) => {
  return string != null ? string.trim().toLowerCase() : "";
};

export const sort = (arr) => {
  return arr.sort((a, b) => {
    // Convert both values to strings and compare
    return a.localeCompare(b, undefined, { numeric: true });
  });
};

export const filterSchools = (activeSearchTerm, activeFilters, schoolsData) => {
  if (activeSearchTerm === "" && Object.values(activeFilters).every(v => v.length === 0)) {
    return schoolsData;
  }

  let filtered = schoolsData;
  const searchTerm = activeSearchTerm.toLowerCase();

  // --- Search ---
  if (searchTerm !== "") {
    filtered = filtered.filter((x) => {
      return (
        x.properties.title_search.includes(searchTerm) ||
        x.properties.subTitle_search.includes(searchTerm) ||
        x.properties.city_search.includes(searchTerm)
      );
    });
  }

  // --- Solar Ownership ---
  if (activeFilters.solarOwnership?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.solarOwnership.includes(
        solarOwnershipSlug(x.properties.solarOwnership)
      );
    });
  }

  // --- Solar Structure ---
  if (activeFilters.structureType?.length > 0) {
    filtered = filtered.filter((x) => {
      const type = x.properties.structureType || "";
      const types = type
        .toString()
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      return types.some((t) => activeFilters.structureType.includes(t));
    });
  }

  // --- Solar Capacity (System Size) ---
  if (activeFilters.systemSize?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.systemSize.some((y) => {
        const raw = x.properties.systemSize;
        const systemSize =
          parseFloat(raw?.toString().replace(/,/g, "")) || raw;
        if (systemSize == null) return false;

        if (y === "* Powered by a district array" && systemSize === "* Powered by a district array") return true;
        if (y === "0-14" && systemSize < 15) return true;
        if (y === "15-49" && systemSize >= 15 && systemSize <= 49.99) return true;
        if (y === "50-199" && systemSize >= 50 && systemSize <= 199.99) return true;
        if (y === "200-499" && systemSize >= 200 && systemSize <= 499.99) return true;
        if (y === "500" && systemSize >= 500) return true;
        if (y === "solar-hot-water-heater" && systemSize === "SH&C") return true;
        return false;
      });
    });
  }

  // --- Clean Energy Technology ---
  if (activeFilters.cleanEnergyTechnology?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.cleanEnergyTechnology.includes(
        cleanEnergyTechnologySlug(x.properties.cleanEnergyTechnology)
      );
    });
  }

  // --- Year Installed ---
  if (activeFilters.yearInstalled?.length > 0) {
    filtered = filtered.filter((x) => {
      const years = (x.properties.yearInstalled || "")
        .toString()
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((year) => !Number.isNaN(year));
      if (years.length === 0) return false;

      return years.some((year) => {
        return activeFilters.yearInstalled.some((y) => {
          if (y === "before-2001") return year < 2001;
          if (y === "2001-2005") return year >= 2001 && year <= 2005;
          if (y === "2006-2010") return year >= 2006 && year <= 2010;
          if (y === "2011-2015") return year >= 2011 && year <= 2015;
          if (y === "2016-2020") return year >= 2016 && year <= 2020;
          if (y === "2021-2025") return year >= 2021 && year <= 2025;
          return false;
        });
      });
    });
  }

  // --- Case Studies ---
  if (activeFilters.caseStudies?.length > 0) {
    filtered = filtered.filter((x) => x.properties.pin_type === "Case Study");
  }

  // --- Battery Storage ---
  if (activeFilters.batteryStorage?.length === 0) {
    filtered = filtered.filter((x) => x.properties.batteryStorage !== true);
  }

  return filtered;
};

export const parseGrantAmount = (raw) => {
  if (raw == null) return null;
  const parsed = parseFloat(raw.toString().replace(/[$,]/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
};

export const filterS4sGrantees = (activeSearchTerm, activeFilters, s4sData) => {
  if (activeSearchTerm === "" && Object.values(activeFilters).every((v) => v.length === 0)) {
    return s4sData;
  }

  let filtered = s4sData;
  const searchTerm = activeSearchTerm.toLowerCase();

  // --- Search ---
  if (searchTerm !== "") {
    filtered = filtered.filter((x) => {
      return (
        x.properties.title_search.includes(searchTerm) ||
        x.properties.subTitle_search.includes(searchTerm) ||
        x.properties.city_search.includes(searchTerm)
      );
    });
  }

  // --- Grant Fiscal Year ---
  if (activeFilters.grantFiscalYear?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.grantFiscalYear.includes(x.properties.fiscalYear?.toString());
    });
  }

  // --- Solar Capacity ---
  if (activeFilters.solarCapacity?.length > 0) {
    filtered = filtered.filter((x) => {
      const size = parseFloat(x.properties.systemSize?.toString().replace(/,/g, ""));
      if (Number.isNaN(size)) return false;

      return activeFilters.solarCapacity.some((y) => {
        if (y === "0-14") return size < 15;
        if (y === "15-49") return size >= 15 && size <= 49.99;
        if (y === "50-200") return size >= 50 && size <= 200;
        if (y === "200-499") return size > 200 && size <= 499.99;
        if (y === "500") return size >= 500;
        return false;
      });
    });
  }

  // --- Grant Amount ---
  if (activeFilters.grantAmount?.length > 0) {
    filtered = filtered.filter((x) => {
      const amount = parseGrantAmount(x.properties.grantAmount);
      if (amount == null) return false;

      return activeFilters.grantAmount.some((y) => {
        if (y === "0-99999") return amount <= 99999;
        if (y === "100000-199999") return amount >= 100000 && amount <= 199999;
        if (y === "200000-299999") return amount >= 200000 && amount <= 299999;
        if (y === "300000-399999") return amount >= 300000 && amount <= 399999;
        if (y === "400000-499999") return amount >= 400000 && amount <= 499999;
        if (y === "500000") return amount >= 500000;
        return false;
      });
    });
  }

  // --- Solar Ownership ---
  if (activeFilters.solarOwnership?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.solarOwnership.includes(
        solarOwnershipSlug(x.properties.solarOwnership)
      );
    });
  }

  return filtered;
};

export const buildS4sFilterOptions = (s4sData) => {
  const grantFiscalYear = [
    ...new Set(s4sData.map((item) => item.properties?.fiscalYear?.toString()).filter(clean)),
  ].sort(alpha);
  const solarOwnership = buildSolarOwnershipOptions(
    s4sData.map((item) => item.properties?.solarOwnership)
  );

  return { grantFiscalYear, solarOwnership };
};

export const filterMeaGrantees = (activeSearchTerm, activeFilters, meaGranteesData) => {
  if (activeSearchTerm === "" && Object.values(activeFilters).every((v) => v.length === 0)) {
    return meaGranteesData;
  }

  let filtered = meaGranteesData;
  const searchTerm = activeSearchTerm.toLowerCase();

  // --- Search ---
  if (searchTerm !== "") {
    filtered = filtered.filter((x) => {
      return (
        x.properties.title_search.includes(searchTerm) ||
        x.properties.subTitle_search.includes(searchTerm) ||
        x.properties.city_search.includes(searchTerm)
      );
    });
  }

  // --- Clean Energy Technology ---
  // Unlike the other maps, MEA grantees filter on the values as the source data
  // spells them, so a row matches if it lists any of the checked technologies.
  if (activeFilters.cleanEnergyTechnology?.length > 0) {
    filtered = filtered.filter((x) => {
      return normalizeFilter(x.properties.cleanEnergyTechnology).some((tech) =>
        activeFilters.cleanEnergyTechnology.includes(tech)
      );
    });
  }

  // --- Grant Fiscal Year ---
  if (activeFilters.fiscalYear?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.fiscalYear.includes(x.properties.fiscalYear?.toString());
    });
  }

  // --- Grant Amount ---
  if (activeFilters.grantAmount?.length > 0) {
    filtered = filtered.filter((x) => {
      const amount = parseGrantAmount(x.properties.grantAmount);
      if (amount == null) return false;

      return activeFilters.grantAmount.some((y) => {
        if (y === "0-99999") return amount <= 99999;
        if (y === "100000-199999") return amount >= 100000 && amount <= 199999;
        if (y === "200000-299999") return amount >= 200000 && amount <= 299999;
        if (y === "300000-399999") return amount >= 300000 && amount <= 399999;
        if (y === "400000-499999") return amount >= 400000 && amount <= 499999;
        if (y === "500000") return amount >= 500000;
        return false;
      });
    });
  }

  // --- Area of Interest (AOI) ---
  if (activeFilters.aoiCategory?.length > 0) {
    filtered = filtered.filter((x) => {
      return activeFilters.aoiCategory.includes(x.properties.aoiCategory);
    });
  }

  return filtered;
};

export const buildMeaGranteesFilterOptions = (meaGranteesData) => {
  const aoiCategory = [
    ...new Set(meaGranteesData.map((item) => item.properties?.aoiCategory).filter(clean)),
  ].sort(alpha);
  const fiscalYear = [
    ...new Set(meaGranteesData.map((item) => item.properties?.fiscalYear?.toString()).filter(clean)),
  ].sort(alpha);
  // Every distinct technology the data lists, rather than the curated options
  // the other maps use. Options are [slug, label] pairs like the curated ones so
  // the Filters component renders them the same way; here the value is both.
  const cleanEnergyTechnology = [
    ...new Set(
      meaGranteesData.flatMap((item) =>
        normalizeFilter(item.properties?.cleanEnergyTechnology)
      )
    ),
  ]
    .sort(alpha)
    .map((tech) => [tech, tech]);

  return { aoiCategory, fiscalYear, cleanEnergyTechnology };
};

export const filterSchoolsByPinType = (schoolsData, pinType) => {
  return schoolsData.filter((x) => {
    return x.properties?.pin_type === pinType;
  });
};

export const filterSchoolsByState = (schoolsData, stateCode) => {
  return schoolsData.filter((x) => {
    return x.properties?.state === stateCode;
  });
};

export const alpha = (a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

export const clean = (v) =>
    v != null && v !== "" && v !== "Unknown";

export const normalizeFilter = (val) => {
    if (val == null) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .flatMap((v) => v.toString().split(",")) // split comma-separated
      .map((s) => s.trim())
      .filter(clean);
  };

// Solar Ownership options, in display order: single values first, then the
// combination. Each option is [slug, filter label, popup label]: the filter
// label distinguishes the option from its siblings, the popup label describes a
// single school. Labels live here so they read well regardless of how the
// source data spells or orders the underlying values.
export const solarOwnershipOptions = [
  ["direct", "Direct Ownership", "Direct Ownership"],
  ["third-party", "Third-party Ownership", "Third-party Ownership"],
  [
    "direct-third-party",
    "Direct and Third-party Ownership",
    "Direct and Third-party Ownership",
  ],
];

// Clean Energy Technology options, in display order. Like Solar Ownership, a
// row falls into exactly one of these, derived from the technologies listed.
export const cleanEnergyTechnologyOptions = [
  ["solar-pv-only", "Solar PV Only", "Solar PV"],
  ["battery-only", "Battery Energy Storage Only", "Battery Energy Storage"],
  [
    "solar-pv-battery",
    "Solar PV + Battery Energy Storage",
    "Solar PV + Battery Energy Storage",
  ],
];

// Match values loosely: case, hyphenation, a leading "and", and the order of a
// combination all vary in the source data.
const facetKey = (val) =>
  val
    .replace(/^and\s+/i, "")
    .toLowerCase()
    .replace(/[-\s]+/g, " ");

// Which values each facet recognizes. Anything else a row lists is kept aside
// as an extra rather than dropped.
const solarOwnershipMatchers = [
  ["direct", (k) => k.startsWith("direct")],
  ["third-party", (k) => k.startsWith("third party")],
];

const cleanEnergyTechnologyMatchers = [
  ["solar-pv", (k) => k === "solar pv"],
  ["bess", (k) => k === "bess"],
];

// Split a row's value into the recognized values it holds and everything else.
const splitFacetValue = (val, matchers) => {
  const matched = new Set();
  const extras = [];

  normalizeFilter(val).forEach((part) => {
    const hit = matchers.find(([, test]) => test(facetKey(part)));
    if (hit) {
      matched.add(hit[0]);
    } else {
      extras.push(part.replace(/^and\s+/i, ""));
    }
  });

  return { matched, extras };
};

// The popup label for a row: the option's own wording, then any extra values
// the facet does not classify, so nothing the data lists goes unshown.
const facetLabel = (options, slug, extras) => {
  const label = options.find(([optionSlug]) => optionSlug === slug)?.[2];
  return [label, ...extras].filter(Boolean).join(", ");
};

export const solarOwnershipSlug = (val) => {
  const { matched } = splitFacetValue(val, solarOwnershipMatchers);
  const hasDirect = matched.has("direct");
  const hasThirdParty = matched.has("third-party");

  if (hasDirect && hasThirdParty) return "direct-third-party";
  if (hasDirect) return "direct";
  if (hasThirdParty) return "third-party";
  return "";
};

export const solarOwnershipLabel = (val) => {
  const { extras } = splitFacetValue(val, solarOwnershipMatchers);
  return facetLabel(solarOwnershipOptions, solarOwnershipSlug(val), extras);
};

// Only offer the options actually present in the data.
export const buildSolarOwnershipOptions = (values) => {
  const present = new Set(values.map(solarOwnershipSlug).filter(Boolean));
  return solarOwnershipOptions.filter(([slug]) => present.has(slug));
};

export const cleanEnergyTechnologySlug = (val) => {
  const { matched } = splitFacetValue(val, cleanEnergyTechnologyMatchers);
  const hasSolar = matched.has("solar-pv");
  const hasBattery = matched.has("bess");

  if (hasSolar && hasBattery) return "solar-pv-battery";
  if (hasSolar) return "solar-pv-only";
  if (hasBattery) return "battery-only";
  return "";
};

export const cleanEnergyTechnologyLabel = (val) => {
  const { extras } = splitFacetValue(val, cleanEnergyTechnologyMatchers);
  return facetLabel(
    cleanEnergyTechnologyOptions,
    cleanEnergyTechnologySlug(val),
    extras
  );
};

// Only offer the options actually present in the data.
export const buildCleanEnergyTechnologyOptions = (values) => {
  const present = new Set(values.map(cleanEnergyTechnologySlug).filter(Boolean));
  return cleanEnergyTechnologyOptions.filter(([slug]) => present.has(slug));
};

export const buildFilterOptions = (schoolsData) => {
  const filterNames = ["structureType"];

  // flat unique values
  const filterOptions = filterNames.reduce((acc, prop) => {
    const values = schoolsData.flatMap((item) =>
      normalizeFilter(item.properties?.[prop])
    );
    acc[prop] = [...new Set(values)].sort(alpha);
    return acc;
  }, {});

  // Solar Ownership facet: fixed slugs with their own labels
  filterOptions.solarOwnership = buildSolarOwnershipOptions(
    schoolsData.map((item) => item.properties?.solarOwnership)
  );

  // Clean Energy Technology facet: fixed slugs with their own labels
  filterOptions.cleanEnergyTechnology = buildCleanEnergyTechnologyOptions(
    schoolsData.map((item) => item.properties?.cleanEnergyTechnology)
  );

  return filterOptions;
};
